/**
 * Role-based Access Control (RBAC) utilities
 * Manages user permissions and menu access based on user groups/roles
 */

// Define all available user roles
export enum UserRole {
  ADMIN = 'ADMIN',
  LOAN_OFFICER = 'LOAN_OFFICER',
  VERIFICATION_OFFICER = 'VERIFICATION_OFFICER',
  CREDIT_ANALYST = 'CREDIT_ANALYST',
  CREDIT_COMMITTEE = 'CREDIT_COMMITTEE',
  FINANCE_OFFICER = 'FINANCE_OFFICER'
}

// Define permission categories
export enum Permission {
  // Dashboard permissions
  VIEW_DASHBOARD = 'VIEW_DASHBOARD',

  // Loan Book permissions
  VIEW_LOAN_BOOK = 'VIEW_LOAN_BOOK',
  VIEW_PORTFOLIO_SUMMARY = 'VIEW_PORTFOLIO_SUMMARY',
  VIEW_PORTFOLIO_OVERVIEW = 'VIEW_PORTFOLIO_OVERVIEW',

  // Account permissions
  VIEW_ACCOUNTS = 'VIEW_ACCOUNTS',
  CREATE_LOAN_ACCOUNT = 'CREATE_LOAN_ACCOUNT',
  MANAGE_ACCOUNTS = 'MANAGE_ACCOUNTS',

  // Application permissions
  VIEW_APPLICATIONS = 'VIEW_APPLICATIONS',
  VIEW_WORK_ITEMS = 'VIEW_WORK_ITEMS',
  VIEW_LEADS = 'VIEW_LEADS',
  CREATE_APPLICATION = 'CREATE_APPLICATION',
  EDIT_APPLICATION = 'EDIT_APPLICATION',

  // Collections permissions
  VIEW_COLLECTIONS = 'VIEW_COLLECTIONS',
  VIEW_DUE_COLLECTIONS = 'VIEW_DUE_COLLECTIONS',
  VIEW_OVERDUE_COLLECTIONS = 'VIEW_OVERDUE_COLLECTIONS',
  MANAGE_PENALTIES = 'MANAGE_PENALTIES',
  RECORD_PAYMENTS = 'RECORD_PAYMENTS',

  // Reports permissions
  VIEW_REPORTS = 'VIEW_REPORTS',
  VIEW_LOAN_REPORTS = 'VIEW_LOAN_REPORTS',
  VIEW_FINANCIAL_REPORTS = 'VIEW_FINANCIAL_REPORTS',
  VIEW_OPERATIONAL_REPORTS = 'VIEW_OPERATIONAL_REPORTS',
  VIEW_COMPLIANCE_REPORTS = 'VIEW_COMPLIANCE_REPORTS',

  // Loan Processing permissions
  PERFORM_VERIFICATION = 'PERFORM_VERIFICATION',
  PERFORM_CREDIT_ASSESSMENT = 'PERFORM_CREDIT_ASSESSMENT',
  // The Credit Committee's decision, and setting aside a condition it attached
  APPROVE_LOANS = 'APPROVE_LOANS',
  WAIVE_CONDITIONS = 'WAIVE_CONDITIONS',
  MANAGE_LOAN_AGREEMENT = 'MANAGE_LOAN_AGREEMENT',
  VERIFY_COLLATERAL = 'VERIFY_COLLATERAL',
  MANAGE_DISBURSEMENTS = 'MANAGE_DISBURSEMENTS',

  // Customer Management permissions
  VIEW_CUSTOMERS = 'VIEW_CUSTOMERS',
  CREATE_CUSTOMER = 'CREATE_CUSTOMER',
  EDIT_CUSTOMER = 'EDIT_CUSTOMER',
  VIEW_CUSTOMER_DOCUMENTS = 'VIEW_CUSTOMER_DOCUMENTS',

  // Document Management permissions
  VIEW_DOCUMENTS = 'VIEW_DOCUMENTS',
  UPLOAD_DOCUMENTS = 'UPLOAD_DOCUMENTS',
  MANAGE_DOCUMENTS = 'MANAGE_DOCUMENTS',

  // Admin permissions
  MANAGE_USERS = 'MANAGE_USERS',
  SYSTEM_SETTINGS = 'SYSTEM_SETTINGS',
  VIEW_NOTIFICATIONS = 'VIEW_NOTIFICATIONS'
}

// Role-based permission mapping
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: [
    // Admin has access to everything
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_LOAN_BOOK,
    Permission.VIEW_PORTFOLIO_SUMMARY,
    Permission.VIEW_PORTFOLIO_OVERVIEW,
    Permission.VIEW_ACCOUNTS,
    Permission.CREATE_LOAN_ACCOUNT,
    Permission.MANAGE_ACCOUNTS,
    Permission.VIEW_APPLICATIONS,
    Permission.VIEW_WORK_ITEMS,
    Permission.VIEW_LEADS,
    Permission.CREATE_APPLICATION,
    Permission.EDIT_APPLICATION,
    Permission.VIEW_COLLECTIONS,
    Permission.VIEW_DUE_COLLECTIONS,
    Permission.VIEW_OVERDUE_COLLECTIONS,
    Permission.MANAGE_PENALTIES,
    Permission.RECORD_PAYMENTS,
    Permission.VIEW_REPORTS,
    Permission.VIEW_LOAN_REPORTS,
    Permission.VIEW_FINANCIAL_REPORTS,
    Permission.VIEW_OPERATIONAL_REPORTS,
    Permission.VIEW_COMPLIANCE_REPORTS,
    Permission.PERFORM_VERIFICATION,
    Permission.PERFORM_CREDIT_ASSESSMENT,
    Permission.APPROVE_LOANS,
    Permission.WAIVE_CONDITIONS,
    Permission.MANAGE_LOAN_AGREEMENT,
    Permission.VERIFY_COLLATERAL,
    Permission.MANAGE_DISBURSEMENTS,
    Permission.VIEW_CUSTOMERS,
    Permission.CREATE_CUSTOMER,
    Permission.EDIT_CUSTOMER,
    Permission.VIEW_CUSTOMER_DOCUMENTS,
    Permission.VIEW_DOCUMENTS,
    Permission.UPLOAD_DOCUMENTS,
    Permission.MANAGE_DOCUMENTS,
    Permission.MANAGE_USERS,
    Permission.SYSTEM_SETTINGS,
    Permission.VIEW_NOTIFICATIONS
  ],

  [UserRole.LOAN_OFFICER]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_LOAN_BOOK,
    Permission.VIEW_PORTFOLIO_SUMMARY,
    Permission.VIEW_ACCOUNTS,
    Permission.CREATE_LOAN_ACCOUNT,
    Permission.VIEW_APPLICATIONS,
    Permission.VIEW_WORK_ITEMS,
    Permission.VIEW_LEADS,
    Permission.CREATE_APPLICATION,
    Permission.EDIT_APPLICATION,
    Permission.VIEW_COLLECTIONS,
    Permission.VIEW_DUE_COLLECTIONS,
    Permission.RECORD_PAYMENTS,
    Permission.VIEW_REPORTS,
    Permission.VIEW_LOAN_REPORTS,
    Permission.VIEW_OPERATIONAL_REPORTS,
    Permission.VIEW_CUSTOMERS,
    Permission.CREATE_CUSTOMER,
    Permission.EDIT_CUSTOMER,
    Permission.VIEW_CUSTOMER_DOCUMENTS,
    Permission.VIEW_DOCUMENTS,
    Permission.UPLOAD_DOCUMENTS,
    Permission.VIEW_NOTIFICATIONS
  ],

  [UserRole.VERIFICATION_OFFICER]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_APPLICATIONS,
    Permission.VIEW_WORK_ITEMS,
    Permission.PERFORM_VERIFICATION,
    Permission.VERIFY_COLLATERAL,
    Permission.VIEW_CUSTOMERS,
    Permission.VIEW_CUSTOMER_DOCUMENTS,
    Permission.VIEW_DOCUMENTS,
    Permission.UPLOAD_DOCUMENTS,
    Permission.VIEW_REPORTS,
    Permission.VIEW_OPERATIONAL_REPORTS,
    Permission.VIEW_NOTIFICATIONS
  ],

  [UserRole.CREDIT_ANALYST]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_LOAN_BOOK,
    Permission.VIEW_PORTFOLIO_SUMMARY,
    Permission.VIEW_PORTFOLIO_OVERVIEW,
    Permission.VIEW_APPLICATIONS,
    Permission.VIEW_WORK_ITEMS,
    Permission.PERFORM_CREDIT_ASSESSMENT,
    Permission.WAIVE_CONDITIONS,
    Permission.VIEW_CUSTOMERS,
    Permission.VIEW_CUSTOMER_DOCUMENTS,
    Permission.VIEW_DOCUMENTS,
    Permission.VIEW_REPORTS,
    Permission.VIEW_LOAN_REPORTS,
    Permission.VIEW_FINANCIAL_REPORTS,
    Permission.VIEW_COMPLIANCE_REPORTS,
    Permission.VIEW_NOTIFICATIONS
  ],

  // Reviews what credit recommends and decides: approve with an amount, rationale and
  // conditions, or reject.
  [UserRole.CREDIT_COMMITTEE]: [
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_LOAN_BOOK,
    Permission.VIEW_PORTFOLIO_SUMMARY,
    Permission.VIEW_PORTFOLIO_OVERVIEW,
    Permission.VIEW_APPLICATIONS,
    Permission.VIEW_WORK_ITEMS,
    Permission.APPROVE_LOANS,
    Permission.WAIVE_CONDITIONS,
    Permission.VIEW_CUSTOMERS,
    Permission.VIEW_CUSTOMER_DOCUMENTS,
    Permission.VIEW_DOCUMENTS,
    Permission.UPLOAD_DOCUMENTS,
    Permission.VIEW_REPORTS,
    Permission.VIEW_LOAN_REPORTS,
    Permission.VIEW_FINANCIAL_REPORTS,
    Permission.VIEW_COMPLIANCE_REPORTS,
    Permission.VIEW_NOTIFICATIONS
  ],

  [UserRole.FINANCE_OFFICER]: [
    // Finance may waive an approval condition at disbursement (a reason is always required)
    Permission.WAIVE_CONDITIONS,
    Permission.VIEW_DASHBOARD,
    Permission.VIEW_LOAN_BOOK,
    Permission.VIEW_PORTFOLIO_SUMMARY,
    Permission.VIEW_PORTFOLIO_OVERVIEW,
    Permission.VIEW_ACCOUNTS,
    Permission.MANAGE_ACCOUNTS,
    Permission.VIEW_COLLECTIONS,
    Permission.VIEW_DUE_COLLECTIONS,
    Permission.VIEW_OVERDUE_COLLECTIONS,
    Permission.MANAGE_PENALTIES,
    Permission.RECORD_PAYMENTS,
    Permission.MANAGE_DISBURSEMENTS,
    Permission.VIEW_REPORTS,
    Permission.VIEW_FINANCIAL_REPORTS,
    Permission.VIEW_COMPLIANCE_REPORTS,
    Permission.VIEW_NOTIFICATIONS
  ]
};

/**
 * Check if a user has a specific permission
 * @param userGroups - Array of user groups/roles
 * @param permission - Permission to check
 * @returns boolean indicating if user has the permission
 */
export const hasPermission = (userGroups: string[] | undefined, permission: Permission): boolean => {
  if (!userGroups || userGroups.length === 0) {
    return false;
  }

  // Check if any of the user's roles grants this permission
  return userGroups.some(group => {
    const role = group as UserRole;
    const rolePermissions = ROLE_PERMISSIONS[role];
    return rolePermissions?.includes(permission) || false;
  });
};

/**
 * Check if a user has any of the specified permissions
 * @param userGroups - Array of user groups/roles
 * @param permissions - Array of permissions to check
 * @returns boolean indicating if user has at least one of the permissions
 */
export const hasAnyPermission = (userGroups: string[] | undefined, permissions: Permission[]): boolean => {
  if (!userGroups || userGroups.length === 0 || permissions.length === 0) {
    return false;
  }

  return permissions.some(permission => hasPermission(userGroups, permission));
};

/**
 * Check if a user has all of the specified permissions
 * @param userGroups - Array of user groups/roles
 * @param permissions - Array of permissions to check
 * @returns boolean indicating if user has all of the permissions
 */
export const hasAllPermissions = (userGroups: string[] | undefined, permissions: Permission[]): boolean => {
  if (!userGroups || userGroups.length === 0 || permissions.length === 0) {
    return false;
  }

  return permissions.every(permission => hasPermission(userGroups, permission));
};

/**
 * Get all permissions for a user based on their roles
 * @param userGroups - Array of user groups/roles
 * @returns Array of all permissions the user has
 */
export const getUserPermissions = (userGroups: string[] | undefined): Permission[] => {
  if (!userGroups || userGroups.length === 0) {
    return [];
  }

  const allPermissions = new Set<Permission>();

  userGroups.forEach(group => {
    const role = group as UserRole;
    const rolePermissions = ROLE_PERMISSIONS[role];
    if (rolePermissions) {
      rolePermissions.forEach(permission => allPermissions.add(permission));
    }
  });

  return Array.from(allPermissions);
};

/**
 * Check if a user is an admin
 * @param userGroups - Array of user groups/roles
 * @returns boolean indicating if user is an admin
 */
export const isAdmin = (userGroups: string[] | undefined): boolean => {
  return userGroups?.includes(UserRole.ADMIN) || false;
};

/**
 * Check if a user has a specific role
 * @param userGroups - Array of user groups/roles
 * @param role - Role to check
 * @returns boolean indicating if user has the role
 */
export const hasRole = (userGroups: string[] | undefined, role: UserRole): boolean => {
  return userGroups?.includes(role) || false;
};
